import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { TradingPair } from '@/types';
import { exchangeApi } from '@/services/api';
import LoadingSpinner from '@/components/UI/LoadingSpinner';
import ExchangeSelector from './ExchangeSelector';
import toast from 'react-hot-toast';

interface TradingPairFormProps {
  initialData?: TradingPair | null;
  onSubmit: (data: Partial<TradingPair>) => void;
  onCancel: () => void;
}

interface FormData {
  symbol: string;
  baseCurrency: string;
  quoteCurrency: string;
  exchangeName: string;
  targetAllocation: number;
  isActive: boolean;
}

const TradingPairForm: React.FC<TradingPairFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [availableMarkets, setAvailableMarkets] = useState<any[]>([]);
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllMarkets, setShowAllMarkets] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      symbol: initialData?.symbol || '',
      baseCurrency: initialData?.baseCurrency || '',
      quoteCurrency: initialData?.quoteCurrency || '',
      exchangeName: initialData?.exchangeName || '',
      targetAllocation: initialData?.targetAllocation || 0,
      isActive: initialData?.isActive ?? true,
    },
  });

  const selectedExchange = watch('exchangeName');
  const selectedSymbol = watch('symbol');

  // Filter and sort markets based on search term
  const filteredMarkets = useMemo(() => {
    if (!availableMarkets.length) return [];
    
    let filtered = availableMarkets;
    
    // Apply search filter
    if (searchTerm) {
      filtered = availableMarkets.filter(market => 
        market.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        market.base.toLowerCase().includes(searchTerm.toLowerCase()) ||
        market.quote.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort by popularity (major pairs first)
    const majorPairs = ['BTC', 'ETH', 'ADA', 'DOT', 'SOL', 'LINK', 'UNI', 'MATIC', 'AVAX'];
    const majorQuotes = ['EUR', 'USD', 'USDT', 'BTC'];
    
    filtered.sort((a, b) => {
      // Prioritize major base currencies
      const aBaseMajor = majorPairs.includes(a.base);
      const bBaseMajor = majorPairs.includes(b.base);
      if (aBaseMajor !== bBaseMajor) return bBaseMajor ? 1 : -1;
      
      // Prioritize major quote currencies
      const aQuoteMajor = majorQuotes.includes(a.quote);
      const bQuoteMajor = majorQuotes.includes(b.quote);
      if (aQuoteMajor !== bQuoteMajor) return bQuoteMajor ? 1 : -1;
      
      // Alphabetical order
      return a.symbol.localeCompare(b.symbol);
    });
    
    // Limit display to prevent UI performance issues
    const displayLimit = showAllMarkets ? filtered.length : Math.min(filtered.length, 50);
    return filtered.slice(0, displayLimit);
  }, [availableMarkets, searchTerm, showAllMarkets]);

  useEffect(() => {
    if (selectedExchange) {
      fetchMarkets(selectedExchange);
    }
  }, [selectedExchange]);

  useEffect(() => {
    if (selectedSymbol && availableMarkets.length > 0) {
      const market = availableMarkets.find(m => m.symbol === selectedSymbol);
      if (market) {
        setValue('baseCurrency', market.base);
        setValue('quoteCurrency', market.quote);
      }
    }
  }, [selectedSymbol, availableMarkets, setValue]);

  const fetchMarkets = async (exchange: string) => {
    try {
      setLoadingMarkets(true);
      const response = await exchangeApi.getMarkets(exchange);
      
      // ApiClient response structure: { success: true, data: { markets: { markets: [...] } } }
      const marketsData = (response as any).data?.markets?.markets || [];
      console.log(`[TRADING_PAIR_FORM] Fetched ${marketsData.length} markets for ${exchange}:`, marketsData.slice(0, 3));
      
      setAvailableMarkets(marketsData);
      setSearchTerm(''); // Clear search when changing exchange
      setShowAllMarkets(false); // Reset show all when changing exchange
    } catch (error) {
      console.error('Error fetching markets:', error);
      toast.error('Failed to load markets for this exchange');
      setAvailableMarkets([]);
    } finally {
      setLoadingMarkets(false);
    }
  };

  const handleFormSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      
      // Parse symbol to extract base and quote currencies if not already set
      if (data.symbol && !data.baseCurrency && !data.quoteCurrency) {
        const parts = data.symbol.split('/');
        if (parts.length === 2) {
          data.baseCurrency = parts[0];
          data.quoteCurrency = parts[1];
        }
      }

      await onSubmit(data);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to save trading pair');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Exchange Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Exchange *
        </label>
        <ExchangeSelector
          selectedExchanges={selectedExchange ? [selectedExchange] : []}
          onExchangeChange={(exchanges) => setValue('exchangeName', exchanges[0] || '')}
          multiSelect={false}
          showHealthStatus={true}
          showCapabilities={false}
          className="mb-2"
        />
        {errors.exchangeName && (
          <p className="mt-1 text-sm text-red-600">{errors.exchangeName.message}</p>
        )}
        
        {/* Hidden field to register with react-hook-form */}
        <input
          type="hidden"
          {...register('exchangeName', { required: 'Exchange is required' })}
        />
        
        {selectedExchange && (
          <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-700">
            Selected: {selectedExchange.charAt(0).toUpperCase() + selectedExchange.slice(1)}
          </div>
        )}
      </div>

      {/* Trading Pair Selection with Search */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Trading Pair *
        </label>
        {selectedExchange ? (
          loadingMarkets ? (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-sm text-gray-600">Loading markets...</span>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Search Input */}
              <div>
                <input
                  type="text"
                  placeholder="Search trading pairs (e.g., BTC, ETH, BTC/EUR)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              {/* Markets Count Info */}
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  Showing {filteredMarkets.length} of {availableMarkets.length} pairs
                  {searchTerm && ` (filtered by "${searchTerm}")`}
                </span>
                {!showAllMarkets && filteredMarkets.length >= 50 && (
                  <button
                    type="button"
                    onClick={() => setShowAllMarkets(true)}
                    className="text-primary-600 hover:text-primary-700 underline"
                  >
                    Show all results
                  </button>
                )}
              </div>
              
              {/* Trading Pair Select */}
              <select
                {...register('symbol', { required: 'Trading pair is required' })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 max-h-60 overflow-y-auto"
                disabled={isSubmitting}
                size={Math.min(filteredMarkets.length + 1, 10)}
              >
                <option value="">Select a trading pair</option>
                {filteredMarkets.map(market => (
                  <option key={market.symbol} value={market.symbol}>
                    {market.symbol} - {market.base}/{market.quote}
                  </option>
                ))}
              </select>
              
              {/* No results message */}
              {searchTerm && filteredMarkets.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-2">
                  No trading pairs found for "{searchTerm}"
                </div>
              )}
            </div>
          )
        ) : (
          <input
            type="text"
            {...register('symbol', { required: 'Trading pair is required' })}
            placeholder="e.g., BTC/USD"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={isSubmitting}
          />
        )}
        {errors.symbol && (
          <p className="mt-1 text-sm text-red-600">{errors.symbol.message}</p>
        )}
      </div>

      {/* Base and Quote Currencies (read-only when auto-populated) */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Base Currency
          </label>
          <input
            type="text"
            {...register('baseCurrency', { required: 'Base currency is required' })}
            placeholder="BTC"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={isSubmitting}
            readOnly={!!(selectedExchange && availableMarkets.length > 0)}
          />
          {errors.baseCurrency && (
            <p className="mt-1 text-sm text-red-600">{errors.baseCurrency.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quote Currency
          </label>
          <input
            type="text"
            {...register('quoteCurrency', { required: 'Quote currency is required' })}
            placeholder="USD"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={isSubmitting}
            readOnly={!!(selectedExchange && availableMarkets.length > 0)}
          />
          {errors.quoteCurrency && (
            <p className="mt-1 text-sm text-red-600">{errors.quoteCurrency.message}</p>
          )}
        </div>
      </div>

      {/* Target Allocation */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Target Allocation (%) *
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          max="100"
          {...register('targetAllocation', {
            required: 'Target allocation is required',
            min: { value: 0, message: 'Allocation must be at least 0%' },
            max: { value: 100, message: 'Allocation cannot exceed 100%' },
            valueAsNumber: true,
          })}
          placeholder="10.00"
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={isSubmitting}
        />
        {errors.targetAllocation && (
          <p className="mt-1 text-sm text-red-600">{errors.targetAllocation.message}</p>
        )}
        <p className="mt-1 text-sm text-gray-500">
          Percentage of your portfolio to allocate to this trading pair
        </p>
      </div>

      {/* Active Status */}
      <div className="flex items-center">
        <input
          type="checkbox"
          {...register('isActive')}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          disabled={isSubmitting}
        />
        <label className="ml-2 block text-sm text-gray-700">
          Active (enable trading for this pair)
        </label>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={isSubmitting || loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          disabled={isSubmitting || loading}
        >
          {loading ? (
            <div className="flex items-center">
              <LoadingSpinner size="sm" color="white" className="mr-2" />
              Saving...
            </div>
          ) : (
            initialData ? 'Update Trading Pair' : 'Create Trading Pair'
          )}
        </button>
      </div>
    </form>
  );
};

export default TradingPairForm; 